import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, Stack, TextField, InputAdornment, MenuItem, Button, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, Avatar, Typography,
  Alert, Snackbar, CircularProgress, Paper, Tooltip, IconButton, LinearProgress,
  useTheme,
} from '@mui/material';
import HomeWorkIcon        from '@mui/icons-material/HomeWork';
import SearchIcon          from '@mui/icons-material/Search';
import PlayArrowIcon       from '@mui/icons-material/PlayArrow';
import PauseIcon           from '@mui/icons-material/Pause';
import RestartAltIcon      from '@mui/icons-material/RestartAlt';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import QrCode2Icon         from '@mui/icons-material/QrCode2';
import CloseIcon           from '@mui/icons-material/Close';
import PeopleAltIcon       from '@mui/icons-material/PeopleAlt';
import PersonOffIcon       from '@mui/icons-material/PersonOff';
import BeachAccessIcon     from '@mui/icons-material/BeachAccess';
import WifiIcon            from '@mui/icons-material/Wifi';
import WifiOffIcon         from '@mui/icons-material/WifiOff';
import TimerIcon           from '@mui/icons-material/Timer';
import DownloadIcon        from '@mui/icons-material/Download';
import RefreshIcon         from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LoginIcon           from '@mui/icons-material/Login';
import LogoutIcon          from '@mui/icons-material/Logout';
import FreeBreakfastIcon   from '@mui/icons-material/FreeBreakfast';
import GroupsIcon          from '@mui/icons-material/Groups';
import PendingActionsIcon  from '@mui/icons-material/PendingActions';
import OpenInNewIcon       from '@mui/icons-material/OpenInNew';
import MailOutlineIcon     from '@mui/icons-material/MailOutline';
import LocalPhoneIcon      from '@mui/icons-material/LocalPhoneOutlined';
import DescriptionIcon     from '@mui/icons-material/DescriptionOutlined';
import FolderOutlinedIcon  from '@mui/icons-material/FolderOutlined';
import CalendarTodayIcon   from '@mui/icons-material/CalendarTodayOutlined';
import ChevronRightIcon    from '@mui/icons-material/ChevronRight';
import BarChartIcon        from '@mui/icons-material/BarChartOutlined';
import DonutLargeIcon      from '@mui/icons-material/DonutLargeOutlined';
import InfoOutlinedIcon    from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ArrowBackIcon       from '@mui/icons-material/ArrowBack';
import { useHistory, useLocation } from 'react-router-dom';
import apiService          from '../../services/apiServices';
import presenceService     from '../../services/presenceService';
import globals             from '../../services/globals';
import MyQRScreen          from './MyQRScreen';

import usePermissions          from '../../hooks/usePermissions';
import { activeWorkers } from '../../utils/workerStatus';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Bearer headers for the raw fetch() calls below (mirrors the axios client's
// auth interceptor). These endpoints require auth, so omitting it returns 401.
function authHeaders() {
  try {
    const u = globals().getLogedInUser?.();
    const token = u?.token || u?.data?.token || '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch { return {}; }
}

// ─── Utility helpers ─────────────────────────────────────────────────────────
export function fmtTime(s) {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d)) return '';
  return `${('0'+d.getHours()).slice(-2)}:${('0'+d.getMinutes()).slice(-2)}`;
}
export function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${('0'+(d.getMonth()+1)).slice(-2)}-${('0'+d.getDate()).slice(-2)}`;
}
export function loadPresence() {
  try { const m = JSON.parse(localStorage.getItem('presence:'+todayKey())||'{}'); return m&&typeof m==='object'?m:{}; } catch(_){return{};}
}
export function savePresence(map) {
  localStorage.setItem('presence:'+todayKey(), JSON.stringify(map||{}));
}
export function eventsToPresence(events=[]) {
  const norm = (events||[]).map(ev=>{
    const type=String(ev?.Type||ev?.type||ev?.event_type||'').toLowerCase();
    const wid=ev?.WorkerId||ev?.workerId||ev?.worker_id;
    const atRaw=ev?.At||ev?.at||ev?.Timestamp||ev?.timestamp||ev?.created_at;
    const at=atRaw?new Date(atRaw):null;
    return{type,workerId:wid?String(wid):'',at};
  }).filter(e=>e.workerId&&e.at&&!isNaN(e.at));
  norm.sort((a,b)=>a.at-b.at);
  const map={};
  norm.forEach(({type,workerId,at})=>{
    const iso=at.toISOString();
    if(!map[workerId])map[workerId]={breaks:[]};
    const e=map[workerId],brks=[...(e.breaks||[])];
    switch(type){
      case'checkin':case'check-in':case'check_in':case'in':
        // Nëse ka checkout të vjetër dhe checkin i ri është më vonë → reset
        if (e.checkOut && at > new Date(e.checkOut)) {
          e.checkOut = undefined; e.breaks = [];
        }
        if(!e.checkIn||at<new Date(e.checkIn))e.checkIn=iso; break;
      case'checkout':case'check-out':case'check_out':case'out':
        e.checkOut=iso; break;
      case'undo_checkout':case'undo-checkout':
        e.checkOut=undefined; break;
      case'break_start':case'breakstart':case'pause':
        e.breaks=[...brks,{start:iso,type:'break'}]; break;
      case'meeting_start':case'meetingstart':
        e.breaks=[...brks,{start:iso,type:'meeting'}]; break;
      case'lunch_start':case'lunchstart':
        e.breaks=[...brks,{start:iso,type:'lunch'}]; break;
      case'short_break_start':case'shortbreakstart':
        e.breaks=[...brks,{start:iso,type:'short'}]; break;
      case'break_end':case'breakend':case'resume':case'meeting_end':case'meetingend':case'lunch_end':case'lunchend':case'short_break_end':case'shortbreakend':{
        const nb=[...brks]; let paired=false;
        for(let i=nb.length-1;i>=0;i--){if(!nb[i].end){nb[i]={...nb[i],end:iso};paired=true;break;}}
        if(!paired)nb.push({start:iso,end:iso});
        e.breaks=nb; break;
      }
      case'reset': map[workerId]={breaks:[]}; return;
      default: break;
    }
    map[workerId]={...e,breaks:e.breaks||brks};
  });
  return map;
}
export function calcBreakMinutes(breaks=[]) {
  return breaks.reduce((s,b)=>{
    const st=b?.start?new Date(b.start).getTime():NaN,en=b?.end?new Date(b.end).getTime():NaN;
    return(!isNaN(st)&&!isNaN(en)&&en>=st)?s+Math.round((en-st)/60000):s;
  },0);
}
export function statusFromPresence(p={}) {
  if(p.checkOut)return'left';
  const activeBreak=(p.breaks||[]).find(b=>b.start&&!b.end);
  if(activeBreak){
    if(activeBreak.type==='meeting')return'meeting';
    if(activeBreak.type==='lunch')return'lunch';
    if(activeBreak.type==='short')return'short';
    return'break';
  }
  if(p.checkIn)return'present';
  return'absent';
}
export function activeBreakMinutes(p={}) {
  const list=p?.breaks||[];
  for(let i=list.length-1;i>=0;i--){
    const b=list[i];
    if(b?.start&&!b?.end){const st=new Date(b.start).getTime();return isNaN(st)?0:Math.round((Date.now()-st)/60000);}
  }
  return 0;
}
export function totalBreakMinutes(p={}) { return calcBreakMinutes(p?.breaks||[])+activeBreakMinutes(p); }
export function workMinutes(p={}) {
  if(!p?.checkIn)return 0;
  const start=new Date(p.checkIn).getTime(),end=p?.checkOut?new Date(p.checkOut).getTime():Date.now();
  return Math.max(0,Math.round((end-start)/60000)-totalBreakMinutes(p));
}
export function workSeconds(p={}) {
  if(!p?.checkIn)return 0;
  // active break seconds (if currently on break)
  const activeBreakSec=(()=>{
    const list=p?.breaks||[];
    for(let i=list.length-1;i>=0;i--){
      const b=list[i];
      if(b?.start&&!b?.end){const st=new Date(b.start).getTime();return isNaN(st)?0:Math.floor((Date.now()-st)/1000);}
    }
    return 0;
  })();
  const completedBreakSec=calcBreakMinutes(p?.breaks||[])*60;
  const start=new Date(p.checkIn).getTime(),end=p?.checkOut?new Date(p.checkOut).getTime():Date.now();
  return Math.max(0,Math.floor((end-start)/1000)-completedBreakSec-activeBreakSec);
}
export function formatHHMM(mins=0) {
  const m=Math.max(0,Math.round(mins)),h=Math.floor(m/60);
  return`${h}:${('0'+(m%60)).slice(-2)}`;
}
export function formatHHMMSS(secs=0) {
  const s=Math.max(0,Math.floor(secs)),h=Math.floor(s/3600),m=Math.floor((s%3600)/60),ss=s%60;
  return`${h}:${('0'+m).slice(-2)}:${('0'+ss).slice(-2)}`;
}
// Fjalori i vetëm i statuseve të prezencës — e përdor edhe ekrani i murit
// (`pages/PresenceMonitor.js`), që të mos rrëshqasin dy paleta paralele.
export const STATUS_CFG = {
  present: { label:'Prezent',              color:'#047857', bg:'#ECFDF5', border:'#6EE7B7', icon:<CheckCircleIcon   sx={{fontSize:14}}/> },
  break:   { label:'Në pauzë',             color:'#B45309', bg:'#FFFBEB', border:'#FCD34D', icon:<FreeBreakfastIcon sx={{fontSize:14}}/> },
  lunch:   { label:'Pauzë dreke',          color:'#B45309', bg:'#FFF7ED', border:'#FDBA74', icon:<FreeBreakfastIcon sx={{fontSize:14}}/> },
  short:   { label:'Pushim i shkurtër',    color:'#0E7490', bg:'#E0F2FE', border:'#7DD3FC', icon:<TimerIcon        sx={{fontSize:14}}/> },
  meeting: { label:'Takim/Trajnim',        color:'#5B6B8C', bg:'#F1F4F9', border:'#B6C2D4', icon:<GroupsIcon        sx={{fontSize:14}}/> },
  left:    { label:'Ka dalë',              color:'#475569', bg:'#F8FAFC', border:'#CBD5E1', icon:<LogoutIcon        sx={{fontSize:14}}/> },
  absent:  { label:'Mungon',               color:'#B91C1C', bg:'#FEF2F2', border:'#FECACA', icon:<PersonOffIcon     sx={{fontSize:14}}/> },
  leave:   { label:'Me leje',              color:'#0369A1', bg:'#F0F9FF', border:'#7DD3FC', icon:<BeachAccessIcon   sx={{fontSize:14}}/> },
  remote:  { label:'Distancë',             color:'#256878', bg:'#E6F4F8', border:'#9FCEDB', icon:<HomeWorkIcon      sx={{fontSize:14}}/> },
};

// Tri familjet e statuseve. Filtri i tabelës pranon si çelës statusi ashtu
// edhe çelës grupi, ndaj «Shiko listën e plotë» e një kartele s'ka nevojë për
// gjendje të dytë: shkruan `work` aty ku një rresht shkruan `present`.
const STATUS_GROUPS = {
  work:  ['present','meeting','remote'],
  pause: ['break','lunch','short'],
  out:   ['left','absent','leave'],
};
const GROUP_LABEL = { work:'Në punë', pause:'Në pauzë', out:'Jashtë' };

// Sa shpesh rifreskohet vetvetiu. Shiriti poshtë kokës e shkruan pikërisht këtë
// numër — përndryshe teksti dhe timeri rrëshqasin nga njëri-tjetri.
const REFRESH_SECONDS = 10;

// Hollësia që shoqëron një emër te tooltip-i i kokës: ora e hyrjes për ata në
// punë, minutat e pauzës aktive për ata në pauzë, ora e daljes për ata që dolën.
// Për statuset pa kohë (mungon, me leje) mbetet bosh — një vizë e zbrazët nuk
// thotë asgjë.
function heroDetail(key, p = {}) {
  if (['present','meeting','remote'].includes(key)) return p.checkIn ? fmtTime(p.checkIn) : '';
  if (['break','lunch','short'].includes(key))      { const ab = activeBreakMinutes(p); return ab > 0 ? `${ab}m` : ''; }
  if (key === 'left')                               return p.checkOut ? fmtTime(p.checkOut) : '';
  return '';
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function LivePresence() {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  // Faqja ka dy pamje dhe URL-ja i ndan: `/presence-live` është paneli,
  // `/presence-live/lista/<filtri>` është lista. Filtri nuk mbahet më si
  // gjendje e brendshme — kështu «prapa» i shfletuesit kthen te paneli
  // vetvetiu, dhe një listë e filtruar mund të ndahet me link.
  const listFilter = (/^\/presence-live\/lista\/([^/]+)/.exec(location.pathname||'')||[])[1];
  const isList = !!listFilter;
  // Një filtër i panjohur në URL (link i vjetër, gabim shtypi) kthehet te «të
  // gjithë»: përndryshe faqja do të dilte bosh dhe boshi do të lexohej si
  // «askush nuk është në këtë gjendje», që s'është e vërtetë.
  const rawFilter = isList ? decodeURIComponent(listFilter) : 'all';
  const filter = (STATUS_CFG[rawFilter]||STATUS_GROUPS[rawFilter]) ? rawFilter : 'all';
  const openList  = (v)=>history.push(`/presence-live/lista/${encodeURIComponent(v||'all')}`);
  const closeList = ()=>history.push('/presence-live');
  // Ndryshimi i filtrit brenda listës është `replace`: nuk shton hap të ri, që
  // «prapa» të kthejë te paneli e jo te filtri i mëparshëm.
  const setFilter = (v)=>isList
    ? history.replace(`/presence-live/lista/${encodeURIComponent(v||'all')}`)
    : openList(v);
  const { can } = usePermissions();
  const canEdit = can('attendance.manage');

  const [workers,       setWorkers]       = useState([]);
  const [presence,      setPresence]      = useState(()=>loadPresence());
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [q,             setQ]             = useState('');
  const [holidays,      setHolidays]      = useState([]);
  const [tick,          setTick]          = useState(0);
  const [autoRefresh,   setAutoRefresh]   = useState(true);
  const [toast,         setToast]         = useState({open:false,message:'',severity:'success'});
  const [loadingPresence, setLoadingPresence] = useState(false);
  const [qrWorker,      setQrWorker]      = useState(null);
  const [remoteIds,     setRemoteIds]     = useState(new Set());
  const [activityFeed,  setActivityFeed]  = useState([]);
  const [kioskDevices,  setKioskDevices]  = useState([]);
  const [clock,         setClock]         = useState(new Date());

  // Date filter state
  const [dateMode,      setDateMode]      = useState('today'); // 'today'|'single'|'range'
  const [filterDate,    setFilterDate]    = useState(todayKey());
  const [dateFrom,      setDateFrom]      = useState(todayKey());
  const [dateTo,        setDateTo]        = useState(todayKey());
  // refs so refreshPresenceFromServer (useCallback []) always sees current values
  const dateModeRef   = useRef('today');
  const filterDateRef = useRef(todayKey());
  const dateFromRef   = useRef(todayKey());
  const dateToRef     = useRef(todayKey());

  // Projects filter
  const [projects,      setProjects]      = useState([]);
  const [departments,   setDepartments]   = useState([]);
  const [filterProject, setFilterProject] = useState('');

  // Live clock tick every second
  useEffect(()=>{ const id=setInterval(()=>{setClock(new Date());setTick(t=>t+1);},1000); return()=>clearInterval(id); },[]);

  const refreshPresenceFromServer = useCallback(async(withSpinner=false)=>{
    if(withSpinner)setLoadingPresence(true);
    try{
      const mode=dateModeRef.current;
      let events;
      if(mode==='range')       events=await presenceService().getRange(dateFromRef.current, dateToRef.current);
      else if(mode==='single') events=await presenceService().getDay(new Date(filterDateRef.current));
      else                     events=await presenceService().getDay();
      const srv=eventsToPresence(events||[]);
      const isToday=mode==='today';
      const local=isToday?loadPresence():{};
      const merged=Object.keys(srv).length>0?{...srv}:{...local};
      if(Object.keys(srv).length>0&&isToday)Object.entries(local).forEach(([id,e])=>{if(!merged[id])merged[id]=e;});
      setPresence(merged);
      if(isToday) savePresence(merged);
    }catch(_){setPresence(dateModeRef.current==='today'?loadPresence():{});}
    finally{if(withSpinner)setLoadingPresence(false);}
  },[]);

  const refreshActivityFeed = useCallback(async()=>{
    try{
      const r=await fetch(`${API_BASE}/api/attendance/activity-feed?limit=25`,{headers:authHeaders()});
      const data=await r.json();
      setActivityFeed(data.events||[]);
    }catch(_){}
  },[]);

  const refreshKioskStatus = useCallback(async()=>{
    try{
      const r=await fetch(`${API_BASE}/api/kiosk/status`,{headers:authHeaders()});
      const data=await r.json();
      setKioskDevices(data.devices||[]);
    }catch(_){}
  },[]);

  useEffect(()=>{
    // Prezenca është gjendje e çastit: i larguari s'ka si të jetë «jashtë pune».
    apiService().get('workers').then(r=>{if(r?.success)setWorkers(activeWorkers(r.data));});
    apiService().get('holidays').then(r=>{if(r?.success)setHolidays(r.data||[]);});
    apiService().get('projects').then(r=>{if(r?.success)setProjects(r.data||[]);});
    apiService().get('department').then(r=>{if(r?.success)setDepartments(r.data||[]);}).catch(()=>{
      apiService().get('departments').then(r=>{if(r?.success)setDepartments(r.data||[]);}).catch(()=>{});
    });
    const today=new Date().toISOString().slice(0,10);
    fetch(`${API_BASE}/api/remote-work`,{headers:authHeaders()}).then(r=>r.json()).then(r=>{
      if(r?.data){
        const ids=new Set(r.data.filter(p=>!p.revoked_at&&(p.status==='approved'||!p.status)&&
          (p.allowed_dates||[]).some(d=>String(d).slice(0,10)===today)).map(p=>String(p.worker_id)));
        setRemoteIds(ids);
      }
    }).catch(()=>{});
    refreshPresenceFromServer(true);
    refreshActivityFeed();
    refreshKioskStatus();
  },[refreshPresenceFromServer,refreshActivityFeed,refreshKioskStatus]);

  // Auto refresh every 10s
  useEffect(()=>{
    if(!autoRefresh)return;
    const id=setInterval(()=>{
      refreshPresenceFromServer();
      refreshActivityFeed();
      refreshKioskStatus();
    },REFRESH_SECONDS*1000);
    return()=>clearInterval(id);
  },[autoRefresh,refreshPresenceFromServer,refreshActivityFeed,refreshKioskStatus]);

  const setEntry=(id,updater)=>{
    setPresence(prev=>{
      const next={...(prev||{})},base=next[id]||{breaks:[]};
      next[id]=updater({...base}); savePresence(next); return next;
    });
  };
  const doToast=(message,severity='success')=>setToast({open:true,message,severity});

  const onCheckIn      = async(id)=>{ await presenceService().addEvent({type:'checkin',       workerId:id}); setEntry(id,e=>({...e,checkIn:new Date().toISOString(),checkOut:undefined,breaks:[]})); doToast('Hyrja u regjistrua ✅'); refreshActivityFeed(); setTimeout(()=>refreshPresenceFromServer(),1200); };
  const onStartBreak   = async(id)=>{ await presenceService().addEvent({type:'break_start',   workerId:id}); setEntry(id,e=>({...e,breaks:[...(e.breaks||[]),{start:new Date().toISOString(),type:'break'}]})); doToast('Pauza filloi ☕','info'); refreshActivityFeed(); setTimeout(()=>refreshPresenceFromServer(),1200); };
  const onStartMeeting = async(id)=>{ await presenceService().addEvent({type:'meeting_start',  workerId:id}); setEntry(id,e=>({...e,breaks:[...(e.breaks||[]),{start:new Date().toISOString(),type:'meeting'}]})); doToast('Takim/Trajnim filloi 📋','info'); refreshActivityFeed(); setTimeout(()=>refreshPresenceFromServer(),1200); };
  const onEndBreak     = async(id)=>{ await presenceService().addEvent({type:'break_end',     workerId:id}); setEntry(id,e=>{const l=[...(e.breaks||[])];for(let i=l.length-1;i>=0;i--){if(!l[i].end){l[i]={...l[i],end:new Date().toISOString()};break;}}return{...e,breaks:l};}); doToast('U kthye ▶️','info'); refreshActivityFeed(); setTimeout(()=>refreshPresenceFromServer(),1200); };
  const onCheckOut     = async(id)=>{ await presenceService().addEvent({type:'checkout',       workerId:id}); setEntry(id,e=>({...e,checkOut:new Date().toISOString()})); doToast('Daljet u regjistrua ✅'); refreshActivityFeed(); setTimeout(()=>refreshPresenceFromServer(),1200); };
  const onReset        = async(id)=>{ await presenceService().addEvent({type:'reset',           workerId:id}); setEntry(id,_=>({breaks:[]})); doToast('Rivendosur','warning'); refreshActivityFeed(); setTimeout(()=>refreshPresenceFromServer(),1200); };
  const onUndoCheckout = async(id)=>{ await presenceService().addEvent({type:'undo_checkout',   workerId:id}); setEntry(id,e=>({...e,checkOut:undefined})); doToast('Dalja u anulua ↩️','info'); refreshActivityFeed(); setTimeout(()=>refreshPresenceFromServer(),1200); };
  const onStartLunch      = async(id)=>{ await presenceService().addEvent({type:'lunch_start',       workerId:id}); setEntry(id,e=>({...e,breaks:[...(e.breaks||[]),{start:new Date().toISOString(),type:'lunch'}]})); doToast('Pauza e drekës filloi 🍽️','info'); refreshActivityFeed(); setTimeout(()=>refreshPresenceFromServer(),1200); };
  const onStartShortBreak = async(id)=>{ await presenceService().addEvent({type:'short_break_start', workerId:id}); setEntry(id,e=>({...e,breaks:[...(e.breaks||[]),{start:new Date().toISOString(),type:'short'}]})); doToast('Pushim i shkurtër filloi ☕','info'); refreshActivityFeed(); setTimeout(()=>refreshPresenceFromServer(),1200); };

  // Build leave set
  const leaveSet = useMemo(()=>{
    const today=new Date();today.setHours(0,0,0,0);
    const inRange=(s,e)=>{const sd=new Date(s),ed=new Date(e);sd.setHours(0,0,0,0);ed.setHours(0,0,0,0);return sd<=today&&today<=ed;};
    const isOk=h=>{const v=h?.Approve;if(v===true)return true;if(v===false)return false;if(typeof v==='number')return v===1;const s=String(v||'').toLowerCase();return s==='1'||s==='true'||s==='po'||s==='yes';};
    const s=new Set();(holidays||[]).forEach(h=>{if(isOk(h)&&inRange(h.StartDate,h.EndDate))s.add(String(h.Employee||h.EmployeeId||''));});
    return s;
  },[holidays]);

  const derived = useMemo(()=>{
    const lc=String(q||'').toLowerCase();
    return (workers||[]).map(w=>{
      const id=String(w.Id||w.id),p=presence[id]||{};
      const onBreak=(p.breaks||[]).some(b=>b.start&&!b.end);
      let status=statusFromPresence(p);
      if(leaveSet.has(id)&&status==='absent')status='leave';
      if(remoteIds.has(id)&&status==='absent')status='remote';
      return{id,w,p,status,breakMinutes:calcBreakMinutes(p.breaks||[]),activeBreak:activeBreakMinutes(p),workM:workMinutes(p),workS:workSeconds(p),onBreak,isRemote:remoteIds.has(id)};
    }).filter(it=>{
      if(lc){const f=[it.w?.FirstName,it.w?.LastName,it.w?.Email,it.w?.Department].filter(Boolean).map(s=>s.toLowerCase());if(!f.some(s=>s.includes(lc)))return false;}
      if(filterProject){const ids=(it.w?.ProjectIds||[]).map(String);if(!ids.includes(String(filterProject)))return false;}
      if(filter!=='all'){
        const grp=STATUS_GROUPS[filter];
        return grp?grp.includes(it.status):filter===it.status;
      }
      return true;
    }).sort((a,b)=>{
      // Latest activity first: pick the most recent timestamp among checkIn, checkOut, or last break event.
      const lastTs=(p={})=>{
        const times=[p.checkIn,p.checkOut,...(p.breaks||[]).flatMap(b=>[b.start,b.end])].filter(Boolean);
        return times.length?Math.max(...times.map(t=>new Date(t).getTime())):0;
      };
      const ta=lastTs(a.p), tb=lastTs(b.p);
      if(tb!==ta)return tb-ta;            // newest first
      // Workers with no activity yet → sort by name to keep order stable
      return `${a.w?.FirstName||''} ${a.w?.LastName||''}`.localeCompare(`${b.w?.FirstName||''} ${b.w?.LastName||''}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[workers,presence,q,filter,filterProject,leaveSet,remoteIds,tick]);

  const totals = useMemo(()=>{
    const items=(workers||[]).map(w=>{const id=String(w.Id||w.id);return{id,p:presence[id]||{},status:statusFromPresence(presence[id]||{})};});
    const present=items.filter(it=>it.status==='present').length;
    const absent=items.filter(it=>it.status==='absent'&&!leaveSet.has(it.id)&&!remoteIds.has(it.id)).length;
    // Granular break sub-statuses
    const onBreakOnly=items.filter(it=>it.status==='break').length;
    const onLunch=items.filter(it=>it.status==='lunch').length;
    const onShort=items.filter(it=>it.status==='short').length;
    const onBreak=onBreakOnly+onLunch+onShort; // legacy combined (still used by status filter)
    const onMeeting=items.filter(it=>it.status==='meeting').length;
    const left=items.filter(it=>it.status==='left').length;
    const checked=items.filter(it=>it.p?.checkIn&&!leaveSet.has(it.id));
    const totalWork=checked.reduce((a,it)=>a+workMinutes(it.p),0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return{present,absent,onLeave:leaveSet.size,onRemote:remoteIds.size,onBreakOnly,onLunch,onShort,onBreak,onMeeting,left,totalWork,
      inWork:present+onMeeting+remoteIds.size,
      inPause:onBreakOnly+onLunch+onShort,
      outside:left+absent+leaveSet.size};
  },[workers,presence,leaveSet,remoteIds,tick]);

  // ── Workers grouped by status (for inline chip strips in hero) ─────────────
  const workersByStatus = useMemo(()=>{
    const groups={present:[],meeting:[],remote:[],break:[],lunch:[],short:[],left:[],absent:[],leave:[]};
    (workers||[]).forEach(w=>{
      const id=String(w.Id||w.id);
      const p=presence[id]||{};
      let st=statusFromPresence(p);
      if(leaveSet.has(id)&&st==='absent')st='leave';
      if(remoteIds.has(id)&&st==='absent')st='remote';
      if(groups[st])groups[st].push({id,w,p});
    });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[workers,presence,leaveSet,remoteIds,tick]);


  function exportCsv(list){
    const lines=[['Punëtori','Departamenti','Statusi','Hyrja','Pauza(min)','Dalja','Orë pune'].join(',')];
    list.forEach(it=>{
      const vals=[`${it.w?.FirstName||''} ${it.w?.LastName||''}`.trim(),it.w?.Department||'',it.status,
        fmtTime(it.p.checkIn)||'—',(it.breakMinutes+it.activeBreak)||'',fmtTime(it.p.checkOut)||'—',formatHHMM(it.workM)].map(v=>`"${String(v).replace(/"/g,'""')}"`);
      lines.push(vals.join(','));
    });
    const a=document.createElement('a');a.href=URL.createObjectURL(new Blob([lines.join('\r\n')],{type:'text/csv;charset=utf-8;'}));a.download=`presence_${todayKey()}.csv`;a.click();
  }

  const clockStr = `${('0'+clock.getHours()).slice(-2)}:${('0'+clock.getMinutes()).slice(-2)}:${('0'+clock.getSeconds()).slice(-2)}`;
  const dateStr  = clock.toLocaleDateString('sq-AL',{weekday:'long',day:'numeric',month:'long',year:'numeric'});

  // Kiosku ka TRI gjendje, jo dy. Pa asnjë pajisje të regjistruar s'ka çka të
  // jetë "offline": një kompani që nuk e përdor kioskun shihte një OFFLINE të
  // kuq gjithë ditën, sikur diçka të ishte prishur. Mungesa lexohet si mungesë.
  const kioskCount  = kioskDevices.length;
  const kioskUpCount = kioskDevices.filter(d=>d.online).length;
  const kioskUi = kioskCount===0
    ? { label:'Pa kiosk', color:'text.disabled',  Icon:WifiOffIcon, hint:'Asnjë pajisje kiosk e regjistruar' }
    : kioskUpCount>0
      ? { label:'Kiosk aktiv', color:'success.onSoft', Icon:WifiIcon,    hint:`${kioskUpCount} nga ${kioskCount} pajisje online` }
      : { label:'Kiosk offline', color:'error.onSoft', Icon:WifiOffIcon, hint:`${kioskCount} pajisje të regjistruara, asnjë online` };

  // Koka e listës: statuset e marrin identitetin nga STATUS_CFG, familjet nga
  // statusi i tyre i parë, dhe «të gjithë» nga marka — një hyrje për secilën
  // vlerë që mund të hyjë në URL, që faqja të mos mbetet pa titull.
  const LIST_META = {
    all:   { label:'Të gjithë punonjësit', color:'primary.onSoft',      bg:'primary.soft',        icon:<PeopleAltIcon/> },
    work:  { label:GROUP_LABEL.work,       color:STATUS_CFG.present.color, bg:STATUS_CFG.present.bg, icon:<PeopleAltIcon/> },
    pause: { label:GROUP_LABEL.pause,      color:STATUS_CFG.break.color,   bg:STATUS_CFG.break.bg,   icon:<FreeBreakfastIcon/> },
    out:   { label:GROUP_LABEL.out,        color:STATUS_CFG.left.color,    bg:STATUS_CFG.left.bg,    icon:<LogoutIcon/> },
  };
  const listMeta = LIST_META[filter] || STATUS_CFG[filter] || LIST_META.all;

  // Kartelat e kokës. Çelësi i një rreshti është pikërisht vlera e `filter`-it
  // të tabelës, dhe çelësi i kartelës është grupi — prandaj as rreshti, as
  // butoni poshtë nuk kanë nevojë për përkthim: `setFilter(key)`.
  const attendancePct = workers.length?Math.round((totals.inWork/workers.length)*100):0;
  const heroGroups = [
    { key:'work', label:GROUP_LABEL.work, icon:<PeopleAltIcon/>,
      tone:STATUS_CFG.present.color, soft:STATUS_CFG.present.bg, items:[
        { key:'present', sub:'Punonjës aktiv në zyrë',    count:totals.present },
        { key:'meeting', sub:'Në takim ose trajnim',      count:totals.onMeeting },
        { key:'remote',  sub:'Duke punuar nga distanca',  count:totals.onRemote },
      ]},
    { key:'pause', label:GROUP_LABEL.pause, icon:<FreeBreakfastIcon/>,
      tone:STATUS_CFG.break.color, soft:STATUS_CFG.break.bg, items:[
        { key:'break', sub:'Pauzë e rregullt',      count:totals.onBreakOnly },
        { key:'lunch', sub:'Pushim dreke',          count:totals.onLunch },
        { key:'short', sub:'Pushim disaminutësh',   count:totals.onShort },
      ]},
    { key:'out', label:GROUP_LABEL.out, icon:<LogoutIcon/>,
      tone:STATUS_CFG.left.color, soft:STATUS_CFG.left.bg, items:[
        { key:'left',   sub:'Ka dalë nga puna',      count:totals.left },
        { key:'absent', sub:'Nuk ka bërë check-in',  count:totals.absent },
        { key:'leave',  sub:'Në leje të aprovuar',   count:totals.onLeave },
      ]},
  ];

  return(
    <>
    <Box>
      <Stack spacing={2.5}>

        {!isList&&<>
        {/* ── Koka e operacioneve ───────────────────────────────────────────
            Katër kartela grupesh, secila me rreshtat e vet të statuseve dhe
            një derë poshtë («Shiko listën e plotë») që e filtron tabelën me
            gjithë grupin. Kliku mbi një rresht filtron vetëm atë status.
            Ngjyrat vijnë nga STATUS_CFG — i njëjti fjalor si pilulat e
            tabelës dhe si dialogu, jo një paletë e dytë për kokën. */}
        <Paper elevation={0} sx={{borderRadius:3,border:'1px solid',borderColor:'divider',overflow:'hidden'}}>

          {/* Rreshti i sipërm: identiteti · pulsi i ditës · kontrollet */}
          <Stack direction="row" alignItems="center" justifyContent="space-between"
            spacing={2} sx={{px:2.5,py:2,flexWrap:'wrap',rowGap:2}}>

            <Stack direction="row" spacing={1.75} alignItems="center" sx={{minWidth:0}}>
              <Box sx={{width:52,height:52,borderRadius:2.5,flexShrink:0,
                bgcolor:'primary.soft',color:'primary.main',display:'grid',placeItems:'center'}}>
                <PendingActionsIcon sx={{fontSize:26}}/>
              </Box>
              <Box sx={{minWidth:0}}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{fontSize:'1.375rem',fontWeight:800,letterSpacing:'-0.02em',color:'text.primary'}}>
                    Monitorim Live
                  </Typography>
                  <Box sx={{display:'inline-flex',alignItems:'center',gap:0.5,px:0.875,py:0.25,borderRadius:1.5,
                    bgcolor:'success.soft',color:'success.onSoft',flexShrink:0}}>
                    <FiberManualRecordIcon sx={{fontSize:7,
                      animation:'livePulse 1.6s infinite',
                      '@keyframes livePulse':{'0%,100%':{opacity:1},'50%':{opacity:0.25}}}}/>
                    <Typography component="span" sx={{fontSize:'0.6875rem',fontWeight:700,letterSpacing:'0.08em'}}>
                      LIVE
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={0.875} alignItems="center" sx={{mt:0.25,flexWrap:'wrap',rowGap:0.25}}>
                  <CalendarTodayIcon sx={{fontSize:13,color:'text.disabled'}}/>
                  <Typography sx={{fontSize:'0.8125rem',fontWeight:500,color:'text.secondary',textTransform:'capitalize'}}>
                    {dateStr}
                  </Typography>
                  {activityFeed.length>0&&(()=>{
                    const ev=activityFeed[0];
                    return(
                      <>
                        <Box sx={{width:3,height:3,borderRadius:'50%',bgcolor:'grey.400'}}/>
                        <Typography sx={{fontSize:'0.8125rem',color:'text.disabled'}}>Aktiviteti i fundit</Typography>
                        <Typography sx={{fontSize:'0.8125rem',fontWeight:600,color:'text.primary'}}>{ev.name}</Typography>
                        <Typography sx={{fontSize:'0.8125rem',color:'text.secondary',fontVariantNumeric:'tabular-nums'}}>
                          {fmtTime(ev.at)}
                        </Typography>
                      </>
                    );
                  })()}
                </Stack>
              </Box>
            </Stack>

            {/* Pulsi i ditës: sa veta janë në punë tani, nga sa gjithsej */}
            <Stack direction="row" alignItems="center" spacing={2.5}
              sx={{px:2.5,py:1.25,borderRadius:2.5,border:'1px solid',borderColor:'divider',bgcolor:'grey.50'}}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <PeopleAltIcon sx={{fontSize:24,color:'primary.main'}}/>
                <Box>
                  <Typography sx={{fontSize:'1.25rem',fontWeight:800,lineHeight:1.1,color:'text.primary',fontVariantNumeric:'tabular-nums'}}>
                    {totals.inWork} <Box component="span" sx={{color:'text.disabled',fontWeight:600}}>/ {workers.length}</Box>
                  </Typography>
                  <Typography sx={{fontSize:'0.75rem',color:'text.secondary',fontWeight:500}}>Në punë</Typography>
                </Box>
              </Stack>
              <Box sx={{width:'1px',alignSelf:'stretch',my:0.25,bgcolor:'divider'}}/>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box sx={{position:'relative',display:'inline-flex'}}>
                  <CircularProgress variant="determinate" value={100} size={38} thickness={4}
                    sx={{color:'grey.200'}}/>
                  <CircularProgress variant="determinate" value={attendancePct} size={38} thickness={4}
                    sx={{color:attendancePct>0?'success.main':'grey.300',position:'absolute',left:0,
                      '& .MuiCircularProgress-circle':{strokeLinecap:'round'}}}/>
                  <Box sx={{position:'absolute',inset:0,display:'grid',placeItems:'center'}}>
                    <Typography sx={{fontSize:'0.625rem',fontWeight:700,color:'text.primary',fontVariantNumeric:'tabular-nums'}}>
                      {attendancePct}%
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{fontSize:'0.8125rem',fontWeight:500,color:'text.secondary'}}>Prezencë</Typography>
              </Stack>
            </Stack>

            {/* Kontrollet */}
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{flexWrap:'wrap',rowGap:1.25}}>
              <Tooltip title={kioskUi.hint}>
                <Stack direction="row" spacing={0.875} alignItems="center"
                  sx={{px:1.5,py:1,borderRadius:2,border:'1px solid',borderColor:'divider',bgcolor:'grey.50'}}>
                  <kioskUi.Icon sx={{fontSize:15,color:kioskUi.color}}/>
                  <Typography sx={{fontSize:'0.8125rem',fontWeight:600,color:kioskUi.color}}>{kioskUi.label}</Typography>
                  <Box sx={{width:6,height:6,borderRadius:'50%',bgcolor:kioskUi.dot}}/>
                </Stack>
              </Tooltip>

              <Stack direction="row" alignItems="center"
                divider={<Box sx={{width:'1px',alignSelf:'stretch',my:0.75,bgcolor:'divider'}}/>}
                sx={{border:'1px solid',borderColor:'divider',borderRadius:2,bgcolor:'grey.50'}}>
                <Box sx={{px:1.5,py:1}}>
                  <Typography sx={{fontSize:'1rem',fontWeight:700,color:'text.primary',
                    fontVariantNumeric:'tabular-nums',letterSpacing:'0.02em',lineHeight:1.2}}>
                    {clockStr}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.25} alignItems="center" sx={{px:1}}>
                  <FormControlLabel
                    control={<Switch size="small" checked={autoRefresh} onChange={e=>setAutoRefresh(e.target.checked)}/>}
                    label={<Typography sx={{fontSize:'0.75rem',fontWeight:500,color:'text.secondary'}}>Auto</Typography>}
                    labelPlacement="start"
                    sx={{m:0,'& .MuiFormControlLabel-label':{mr:0.75}}}/>
                  <Tooltip title="Rifresko">
                    <span>
                      <IconButton size="small" disabled={loadingPresence}
                        onClick={()=>{refreshPresenceFromServer(true);refreshActivityFeed();refreshKioskStatus();}}
                        sx={{width:30,height:30,borderRadius:1.5,color:'text.secondary'}}>
                        {loadingPresence?<CircularProgress size={14}/>:<RefreshIcon sx={{fontSize:17}}/>}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Eksporto CSV">
                    <IconButton size="small" onClick={()=>exportCsv(derived)}
                      sx={{width:30,height:30,borderRadius:1.5,color:'text.secondary'}}>
                      <DownloadIcon sx={{fontSize:17}}/>
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              <Tooltip title="Hap ekranin e monitorimit në dritare të veçantë">
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon sx={{fontSize:16}}/>}
                  onClick={()=>{
                    try{
                      const u=globals().getLogedInUser?.();
                      const t=u?.token||u?.data?.token||'';
                      if(t) localStorage.setItem('monitoring_token',t);
                    }catch(_){}
                    window.open(
                      '#/monitoring',
                      'nexuma_monitoring',
                      'width=1280,height=800,resizable=yes,scrollbars=yes'
                    );
                  }}
                  sx={{textTransform:'none',fontWeight:600,fontSize:'0.8125rem',borderRadius:2,px:1.75,py:1,
                    whiteSpace:'nowrap','& .MuiButton-startIcon':{mr:0.75}}}>
                  Ekrani i monitorimit
                </Button>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Kartelat e grupeve */}
          {/* Katër kartela bien në një rresht vetëm kur ka vërtet vend: nën
              1500px një kolonë 260px e pret «Pushim disaminutësh» në mes.
              Prandaj pika e thyerjes matet nga përmbajtja, jo nga `lg`. */}
          <Box sx={{px:2.5,pb:2.5,display:'grid',gap:2,
            gridTemplateColumns:{xs:'1fr',sm:'repeat(2,1fr)'},
            '@media (min-width:1500px)':{gridTemplateColumns:'repeat(4,1fr)'}}}>

            {heroGroups.map(group=>(
              <Box key={group.key} sx={{display:'flex',flexDirection:'column',
                borderRadius:2.5,border:'1px solid',borderColor:'divider',bgcolor:'background.paper',p:2}}>
                <Stack direction="row" spacing={1.25} alignItems="center"
                  sx={{pb:1.5,mb:0.5,borderBottom:'1px solid',borderColor:'divider'}}>
                  {React.cloneElement(group.icon,{sx:{fontSize:20,color:group.tone}})}
                  <Typography sx={{fontSize:'0.8125rem',fontWeight:700,letterSpacing:'0.06em',
                    textTransform:'uppercase',color:'text.primary'}}>
                    {group.label}
                  </Typography>
                </Stack>

                <Stack sx={{flex:1}}>
                  {group.items.map((m,mi)=>{
                    const cfg=STATUS_CFG[m.key];
                    const people=(workersByStatus[m.key]||[]).map(it=>({
                      name:`${it.w?.FirstName||''} ${it.w?.LastName||''}`.trim()||'—',
                      detail:heroDetail(m.key,it.p),
                    }));
                    const row=(
                      <Box
                        component="button"
                        type="button"
                        onClick={()=>openList(m.key)}
                        aria-label={`${cfg.label} — hap listën`}
                        sx={{
                          display:'flex',alignItems:'center',gap:1.5,width:'100%',m:0,
                          px:1,py:1.25,borderRadius:2,textAlign:'left',font:'inherit',
                          border:'1px solid',borderColor:'transparent',bgcolor:'transparent',
                          borderBottom:'1px solid',
                          borderBottomColor:mi<group.items.length-1?'divider':'transparent',
                          cursor:'pointer',transition:theme.motion.hover,
                          '&:hover':{bgcolor:'grey.50'},
                          ...theme.focusRing,
                        }}>
                        <Box sx={{width:40,height:40,borderRadius:2,flexShrink:0,display:'grid',placeItems:'center',
                          bgcolor:cfg.bg,color:cfg.color,border:'1px solid',borderColor:cfg.border}}>
                          {React.cloneElement(cfg.icon,{sx:{fontSize:19}})}
                        </Box>
                        <Box sx={{flex:1,minWidth:0}}>
                          <Typography component="div" noWrap
                            sx={{fontSize:'0.9375rem',fontWeight:600,color:'text.primary',lineHeight:1.3}}>
                            {cfg.label}
                          </Typography>
                          <Typography component="div" noWrap
                            sx={{fontSize:'0.75rem',color:'text.secondary',lineHeight:1.4}}>
                            {m.sub}
                          </Typography>
                        </Box>
                        <Typography component="span"
                          sx={{fontSize:'1.375rem',fontWeight:700,fontVariantNumeric:'tabular-nums',
                            color:m.count>0?cfg.color:'text.disabled'}}>
                          {m.count}
                        </Typography>
                      </Box>
                    );
                    if(!people.length) return row;
                    return(
                      <Tooltip key={m.key} placement="top"
                        title={
                          <Box sx={{py:0.25}}>
                            {people.slice(0,12).map((pp,i)=>(
                              <Typography key={i} component="div" sx={{fontSize:'0.75rem',lineHeight:1.7}}>
                                {pp.name}{pp.detail?` · ${pp.detail}`:''}
                              </Typography>
                            ))}
                            {people.length>12&&(
                              <Typography component="div" sx={{fontSize:'0.75rem',opacity:0.75,mt:0.25}}>
                                +{people.length-12} të tjerë
                              </Typography>
                            )}
                          </Box>
                        }>
                        {row}
                      </Tooltip>
                    );
                  })}
                </Stack>

                {/* Dera e grupit: e njëjta gjendje si rreshtat, vetëm më e gjerë */}
                <Button
                  fullWidth
                  onClick={()=>openList(group.key)}
                  endIcon={<ChevronRightIcon sx={{fontSize:18}}/>}
                  sx={{mt:1.5,textTransform:'none',fontWeight:600,fontSize:'0.8125rem',borderRadius:2,py:1,
                    color:group.tone,bgcolor:group.soft,border:'1px solid',borderColor:'transparent',
                    '&:hover':{bgcolor:group.soft,borderColor:group.tone},
                    '& .MuiButton-endIcon':{ml:0.5}}}>
                  Shiko listën e plotë
                </Button>
              </Box>
            ))}

            {/* Përmbledhja: tri shifra që nuk janë status, ndaj s'kanë filtër */}
            <Box sx={{display:'flex',flexDirection:'column',
              borderRadius:2.5,border:'1px solid',borderColor:'divider',bgcolor:'background.paper',p:2}}>
              <Stack direction="row" spacing={1.25} alignItems="center"
                sx={{pb:1.5,mb:1.5,borderBottom:'1px solid',borderColor:'divider'}}>
                <BarChartIcon sx={{fontSize:20,color:'primary.main'}}/>
                <Typography sx={{fontSize:'0.8125rem',fontWeight:700,letterSpacing:'0.06em',
                  textTransform:'uppercase',color:'text.primary'}}>
                  Përmbledhje
                </Typography>
              </Stack>

              <Stack spacing={1.25} sx={{flex:1}}>
                {[
                  {Icon:PeopleAltIcon, value:String(workers.length),         label:'Punonjës gjithsej'},
                  {Icon:TimerIcon,     value:formatHHMM(totals.totalWork),   label:'Orë pune sot'},
                  {Icon:DonutLargeIcon,value:`${attendancePct}%`,            label:'Prezencë'},
                ].map(({Icon,value,label})=>(
                  <Stack key={label} direction="row" spacing={1.5} alignItems="center"
                    sx={{px:1.5,py:1.25,borderRadius:2,bgcolor:'primary.soft'}}>
                    <Icon sx={{fontSize:22,color:'primary.onSoft',flexShrink:0}}/>
                    <Box sx={{minWidth:0}}>
                      <Typography sx={{fontSize:'1.25rem',fontWeight:800,lineHeight:1.15,color:'primary.onSoft',
                        fontVariantNumeric:'tabular-nums'}}>
                        {value}
                      </Typography>
                      <Typography sx={{fontSize:'0.75rem',fontWeight:500,color:'text.secondary'}} noWrap>
                        {label}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                onClick={()=>history.push('/home/reports/presence')}
                endIcon={<ChevronRightIcon sx={{fontSize:18}}/>}
                sx={{mt:1.5,textTransform:'none',fontWeight:600,fontSize:'0.8125rem',borderRadius:2,py:1,
                  '& .MuiButton-endIcon':{ml:0.5}}}>
                Raporti i plotë
              </Button>
            </Box>
          </Box>

          {/* Shiriti i shpjegimeve. Intervali shkruhet nga i njëjti konstant që
              e ushqen timerin — një numër i shkruar me dorë këtu do të gënjente
              ditën që ndryshon timeri, dhe me «Auto» të fikur nuk përditësohet
              asgjë. */}
          <Stack direction={{xs:'column',md:'row'}} spacing={2} alignItems={{md:'center'}}
            sx={{px:2.5,py:2,borderTop:'1px solid',borderColor:'divider',bgcolor:'grey.50'}}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{flex:1,minWidth:0}}>
              <Box sx={{width:36,height:36,borderRadius:2,flexShrink:0,display:'grid',placeItems:'center',
                bgcolor:'info.soft',color:'info.onSoft'}}>
                <InfoOutlinedIcon sx={{fontSize:19}}/>
              </Box>
              <Box sx={{minWidth:0}}>
                <Typography sx={{fontSize:'0.875rem',fontWeight:700,color:'text.primary',lineHeight:1.3}}>
                  Informacion
                </Typography>
                <Typography sx={{fontSize:'0.8125rem',color:'text.secondary'}}>
                  {autoRefresh
                    ? `Të dhënat përditësohen automatikisht çdo ${REFRESH_SECONDS} sekonda.`
                    : 'Përditësimi automatik është i fikur — përdor butonin e rifreskimit.'}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.25} alignItems="center"
              sx={{px:1.75,py:1.25,borderRadius:2,bgcolor:'primary.soft',color:'primary.onSoft',flexShrink:0}}>
              <LightbulbOutlinedIcon sx={{fontSize:18}}/>
              <Typography sx={{fontSize:'0.8125rem',fontWeight:500}}>
                Kliko mbi çdo kategori për të parë listën e detajuar të punonjësve.
              </Typography>
            </Stack>
          </Stack>

          {loadingPresence&&<LinearProgress sx={{height:2}}/>}
        </Paper>
        </>}

        {/* ── Lista e filtruar ─────────────────────────────────────────────
            Tabela nuk rri më nën panel: hapet si faqe e vetën kur klikon një
            status ose një familje. Prandaj koka e saj duhet të thotë tri gjëra
            që panelit s'i duheshin — çfarë po shikon, sa veta janë, dhe si
            kthehesh. */}
        {isList&&(
        <>
          <Paper elevation={0} sx={{p:2,borderRadius:2.5,border:'1px solid',borderColor:'divider'}}>
            <Stack direction="row" alignItems="center" spacing={1.75} sx={{flexWrap:'wrap',rowGap:1.5}}>
              <Tooltip title="Kthehu te monitorimi">
                <IconButton onClick={closeList}
                  sx={{width:40,height:40,borderRadius:2,border:'1px solid',borderColor:'divider',color:'text.secondary',
                    '&:hover':{bgcolor:'grey.50',color:'text.primary'}}}>
                  <ArrowBackIcon sx={{fontSize:19}}/>
                </IconButton>
              </Tooltip>
              <Box sx={{width:44,height:44,borderRadius:2,flexShrink:0,display:'grid',placeItems:'center',
                bgcolor:listMeta.bg,color:listMeta.color}}>
                {React.cloneElement(listMeta.icon,{sx:{fontSize:21}})}
              </Box>
              <Box sx={{minWidth:0,flex:1}}>
                <Typography sx={{fontSize:'1.125rem',fontWeight:800,color:'text.primary',lineHeight:1.25}} noWrap>
                  {listMeta.label}
                </Typography>
                <Typography sx={{fontSize:'0.8125rem',color:'text.secondary'}}>
                  {derived.length} punonjës · <Box component="span" sx={{textTransform:'capitalize'}}>{dateStr}</Box>
                </Typography>
              </Box>
              <Button variant="outlined" onClick={()=>exportCsv(derived)} startIcon={<DownloadIcon sx={{fontSize:16}}/>}
                sx={{textTransform:'none',fontWeight:600,fontSize:'0.8125rem',borderRadius:2,px:1.75,py:0.875,
                  whiteSpace:'nowrap','& .MuiButton-startIcon':{mr:0.75}}}>
                Eksporto CSV
              </Button>
            </Stack>
          </Paper>
        {/* ── Filters + Table (primary content) ─────────────────────────────── */}
        <Box>
          <Stack spacing={1.5}>

              {/* Toolbar — single compact row */}
              <Paper elevation={0} sx={{p:1.25,borderRadius:2,border:'1px solid',borderColor:'divider'}}>
                <Stack direction={{xs:'column',md:'row'}} spacing={1} alignItems={{md:'center'}} sx={{flexWrap:{md:'wrap'},rowGap:1}}>
                  {/* Search */}
                  <TextField placeholder="Kërko emër, email, departament…" value={q} onChange={e=>setQ(e.target.value)}
                    size="small" sx={{flex:1,minWidth:200,'& .MuiOutlinedInput-root':{borderRadius:1.5,height:38,bgcolor:'grey.50'},'& .MuiOutlinedInput-input':{py:0}}}
                    InputProps={{startAdornment:<InputAdornment position="start"><SearchIcon sx={{fontSize:18,color:'text.disabled'}}/></InputAdornment>}}/>

                  {/* Date mode segmented control */}
                  <Box sx={{display:'inline-flex',border:1,borderColor:'divider',borderRadius:1.5,p:0.375,bgcolor:'background.paper',flexShrink:0}}>
                    {[{v:'today',l:'Sot'},{v:'single',l:'Datë'},{v:'range',l:'Periudhë'}].map(({v,l})=>{
                      const active=dateMode===v;
                      return(
                        <Box key={v}
                          onClick={()=>{dateModeRef.current=v;setDateMode(v);setTimeout(()=>refreshPresenceFromServer(true),50);}}
                          sx={{px:1.25,py:0.5,borderRadius:1,fontSize:'0.75rem',fontWeight:500,cursor:'pointer',
                            bgcolor:active?'primary.soft':'transparent',color:active?'primary.dark':'text.secondary',
                            transition:'background-color 0.12s ease',
                            '&:hover':{bgcolor:active?'primary.soft':'grey.50'}}}>
                          {l}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Date pickers (conditional) */}
                  {dateMode==='single'&&(
                    <TextField type="date" size="small" value={filterDate}
                      onChange={e=>{filterDateRef.current=e.target.value;setFilterDate(e.target.value);setTimeout(()=>refreshPresenceFromServer(true),50);}}
                      sx={{minWidth:140,'& .MuiOutlinedInput-root':{borderRadius:1.5,height:38},'& .MuiOutlinedInput-input':{py:0,fontSize:'0.8125rem'}}}/>
                  )}
                  {dateMode==='range'&&(
                    <>
                      <TextField type="date" size="small" label="Nga" value={dateFrom} InputLabelProps={{shrink:true}}
                        onChange={e=>{dateFromRef.current=e.target.value;setDateFrom(e.target.value);if(e.target.value<=dateToRef.current)setTimeout(()=>refreshPresenceFromServer(true),50);}}
                        sx={{minWidth:140,'& .MuiOutlinedInput-root':{borderRadius:1.5,height:38},'& .MuiOutlinedInput-input':{py:0,fontSize:'0.8125rem'}}}/>
                      <TextField type="date" size="small" label="Deri" value={dateTo} InputLabelProps={{shrink:true}}
                        onChange={e=>{dateToRef.current=e.target.value;setDateTo(e.target.value);if(e.target.value>=dateFromRef.current)setTimeout(()=>refreshPresenceFromServer(true),50);}}
                        sx={{minWidth:140,'& .MuiOutlinedInput-root':{borderRadius:1.5,height:38},'& .MuiOutlinedInput-input':{py:0,fontSize:'0.8125rem'}}}/>
                    </>
                  )}

                  {/* Filtri i projekteve. Pa `displayEmpty` MUI-ja e vizaton
                      vlerën boshe si hapësirë zero-gjerësi: kutia dilte krejt
                      bosh, pa asnjë shenjë se çfarë filtron. Tani gjendja
                      «pa filtër» e thotë veten, si te filtri i statusit. */}
                  {projects.length>0&&(
                    <TextField select value={filterProject} onChange={e=>setFilterProject(e.target.value)} size="small"
                      SelectProps={{displayEmpty:true,renderValue:(v)=>{
                        const pr=projects.find(x=>String(x.Id||x.id)===String(v));
                        return(
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <FolderOutlinedIcon sx={{fontSize:15,color:'text.disabled'}}/>
                            <Typography sx={{fontSize:'0.8125rem',fontWeight:pr?600:500,
                              color:pr?'text.primary':'text.secondary'}} noWrap>
                              {pr?(pr.Name||pr.name):'Të gjitha projektet'}
                            </Typography>
                          </Stack>
                        );
                      }}}
                      sx={{minWidth:180,'& .MuiOutlinedInput-root':{borderRadius:1.5,height:38},'& .MuiOutlinedInput-input':{py:0,display:'flex',alignItems:'center',fontSize:'0.8125rem'}}}>
                      <MenuItem value=""><Typography sx={{fontSize:'0.8125rem',color:'text.secondary'}}>Të gjitha projektet</Typography></MenuItem>
                      {projects.map(pr=>(
                        <MenuItem key={pr.Id} value={String(pr.Id)}>
                          <Typography sx={{fontSize:'0.8125rem'}}>{pr.Name}</Typography>
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {/* Status filter — each status shows its own granular count */}
                  <TextField select value={filter} onChange={e=>setFilter(e.target.value)} size="small"
                    sx={{minWidth:180,'& .MuiOutlinedInput-root':{borderRadius:1.5,height:38},'& .MuiOutlinedInput-input':{py:0,display:'flex',alignItems:'center',fontSize:'0.8125rem'}}}>
                    {[{v:'all',label:`Të gjithë`,count:workers.length,icon:<PeopleAltIcon sx={{fontSize:15,color:'text.secondary'}}/>},
                      // Familjet: e njëjta gjendje që vendosin butonat e kartelave.
                      {v:'work', label:GROUP_LABEL.work, count:totals.inWork,
                        icon:<PeopleAltIcon      sx={{fontSize:15,color:STATUS_CFG.present.color}}/>},
                      {v:'pause',label:GROUP_LABEL.pause,count:totals.inPause,
                        icon:<FreeBreakfastIcon  sx={{fontSize:15,color:STATUS_CFG.break.color}}/>},
                      {v:'out',  label:GROUP_LABEL.out,  count:totals.outside,
                        icon:<LogoutIcon         sx={{fontSize:15,color:STATUS_CFG.left.color}}/>},
                      ...Object.entries(STATUS_CFG).map(([v,c])=>({v,label:c.label,count:
                        v==='present'?totals.present:
                        v==='break'?totals.onBreakOnly:
                        v==='lunch'?totals.onLunch:
                        v==='short'?totals.onShort:
                        v==='meeting'?totals.onMeeting:
                        v==='absent'?totals.absent:
                        v==='leave'?totals.onLeave:
                        v==='remote'?totals.onRemote:
                        v==='left'?totals.left:0,
                        icon:React.cloneElement(c.icon,{sx:{fontSize:15,color:c.color}}),color:c.color}))
                    ].map(({v,label,count,icon})=>(
                      <MenuItem key={v} value={v}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{width:'100%'}}>
                          {icon}
                          <Typography sx={{fontSize:'0.8125rem',fontWeight:filter===v?600:500,flex:1}}>{label}</Typography>
                          {count!==undefined&&<Typography sx={{fontSize:'0.75rem',color:'text.disabled'}}>{count}</Typography>}
                        </Stack>
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Paper>

              {/* Table */}
              <Card elevation={0} sx={{borderRadius:2,border:'1px solid',borderColor:'divider',overflow:'hidden'}}>
                <Table sx={{tableLayout:'fixed'}}>
                  <TableHead>
                    <TableRow>
                      {[
                        // Kolonat me kohë janë numra: koka dhe vlera shkojnë
                        // djathtas me `tabular-nums`, që shifrat të bien në një
                        // shtyllë të vetme (konventa e ReportKit-it).
                        {label:'Punëtori',     width:'24%', align:'left',  display:'table-cell'},
                        {label:'Departamenti', width:'13%', align:'left',  display:{xs:'none',lg:'table-cell'}},
                        {label:'Projekti',     width:'13%', align:'left',  display:{xs:'none',lg:'table-cell'}},
                        {label:'Statusi',      width:'14%', align:'left',  display:'table-cell'},
                        {label:'Hyrja',        width:'9%',  align:'right', display:{xs:'none',sm:'table-cell'}},
                        {label:'Pauza',        width:'8%',  align:'right', display:{xs:'none',md:'table-cell'}},
                        {label:'Dalja',        width:'9%',  align:'right', display:{xs:'none',sm:'table-cell'}},
                        {label:'Orë pune',     width:'10%', align:'right', display:'table-cell'},
                      ].map((h)=>(
                        <TableCell key={h.label} align={h.align} sx={{
                          width:h.width,
                          fontWeight:600,fontSize:'0.6875rem',letterSpacing:'0.06em',textTransform:'uppercase',
                          color:'text.secondary',py:1.25,bgcolor:'grey.50',
                          borderBottom:1,borderColor:'divider',display:h.display,
                        }}>{h.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {derived.map((it)=>{
                      const cfg=STATUS_CFG[it.status]||STATUS_CFG.absent;
                      const name=`${it.w?.FirstName||''} ${it.w?.LastName||''}`.trim();
                      const init=name.split(' ').filter(Boolean).map(x=>x[0]).join('').slice(0,2).toUpperCase();

                      // Department: resolve ID → name when possible; else use string value; else empty
                      const rawDept=it.w?.Department||it.w?.department||'';
                      const deptObj=departments.find(d=>String(d.Id||d.id)===String(rawDept));
                      const deptName=deptObj?.Name||deptObj?.name||(rawDept&&!/^\d+$/.test(String(rawDept).trim())?rawDept:'');

                      // Projects: resolve worker's ProjectIds[] → project names
                      const workerProjectIds=(it.w?.ProjectIds||it.w?.projectIds||[]).map(String);
                      const workerProjects=projects.filter(p=>workerProjectIds.includes(String(p.Id||p.id)));
                      const projectNames=workerProjects.map(p=>p.Name||p.name).filter(Boolean);

                      const workPct=Math.min(100,Math.round((it.workM/(8*60))*100));
                      const onLeave=it.status==='leave';
                      return(
                        <TableRow key={it.id} onClick={()=>setSelectedWorker(it)} sx={{
                          '&:last-child td':{border:0},
                          '& td':{borderColor:'divider'},
                          '&:hover':{bgcolor:'action.hover'},
                          opacity:onLeave?0.7:1,
                          transition:theme.motion.hover,
                          cursor:'pointer',
                        }}>
                          {/* Worker */}
                          <TableCell sx={{py:1.25}}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar sx={{width:34,height:34,bgcolor:'primary.soft',color:'primary.onSoft',fontSize:'0.8125rem',fontWeight:700,flexShrink:0}}>
                                {init}
                              </Avatar>
                              <Box sx={{minWidth:0}}>
                                <Typography sx={{fontSize:'0.9375rem',fontWeight:600,color:'text.primary',letterSpacing:'-0.005em',lineHeight:1.3}} noWrap>{name||'—'}</Typography>
                                {it.w?.Position&&<Typography sx={{fontSize:'0.75rem',color:'text.secondary',fontWeight:500,mt:0.25}} noWrap>{it.w.Position}</Typography>}
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* Departamenti */}
                          <TableCell sx={{py:1.25,display:{xs:'none',lg:'table-cell'}}}>
                            {deptName
                              ? <Typography sx={{fontSize:'0.875rem',fontWeight:500,color:'text.primary'}} noWrap>{deptName}</Typography>
                              : <Typography sx={{fontSize:'0.875rem',color:'text.disabled'}}>—</Typography>}
                          </TableCell>

                          {/* Projekti */}
                          <TableCell sx={{py:1.25,display:{xs:'none',lg:'table-cell'}}}>
                            {projectNames.length===0
                              ? <Typography sx={{fontSize:'0.875rem',color:'text.disabled'}}>—</Typography>
                              : projectNames.length===1
                                ? <Typography sx={{fontSize:'0.875rem',fontWeight:500,color:'text.primary'}} noWrap>{projectNames[0]}</Typography>
                                : <Tooltip title={projectNames.join(', ')}>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Typography sx={{fontSize:'0.875rem',fontWeight:500,color:'text.primary'}} noWrap>{projectNames[0]}</Typography>
                                      <Chip size="small" label={`+${projectNames.length-1}`} sx={{height:18,fontWeight:600,fontSize:'0.6875rem',bgcolor:'grey.100',color:'text.secondary',border:0,'& .MuiChip-label':{px:0.625}}}/>
                                    </Stack>
                                  </Tooltip>}
                          </TableCell>

                          {/* Status */}
                          <TableCell sx={{py:1.25}}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Chip size="small" label={cfg.label}
                                icon={React.cloneElement(cfg.icon,{sx:{fontSize:'12px !important',color:`${cfg.color} !important`}})}
                                sx={{bgcolor:cfg.bg,color:cfg.color,fontWeight:600,fontSize:'0.6875rem',border:0,height:22,
                                  '& .MuiChip-icon':{color:`${cfg.color} !important`,ml:0.625},
                                  '& .MuiChip-label':{px:0.875}}}/>
                              {it.isRemote&&it.status!=='remote'&&<Tooltip title="Ka leje remote sot"><HomeWorkIcon sx={{fontSize:14,color:'primary.main'}}/></Tooltip>}
                            </Stack>
                          </TableCell>

                          {/* Check-in */}
                          <TableCell align="right" sx={{py:1.25,display:{xs:'none',sm:'table-cell'}}}>
                            <Typography sx={{fontSize:'0.875rem',fontWeight:600,color:it.p.checkIn&&!onLeave?'text.primary':'text.disabled',fontVariantNumeric:'tabular-nums'}}>
                              {onLeave?'—':fmtTime(it.p.checkIn)||'—'}
                            </Typography>
                          </TableCell>

                          {/* Break */}
                          {/* Pauza. Rrotulluesi u hoq: pilula e statusit e thotë
                              tashmë se pauza është aktive, kurse njëzet rrotullues
                              njëherësh e bëjnë tabelën të vibrojë. Minutat në ton
                              paralajmërimi kur pauza është ende hapur. */}
                          <TableCell align="right" sx={{py:1.25,display:{xs:'none',md:'table-cell'}}}>
                            {onLeave||(!it.onBreak&&(it.breakMinutes+it.activeBreak)===0)
                              ?<Typography sx={{color:'text.disabled'}}>—</Typography>
                              :<Typography sx={{fontSize:'0.875rem',fontWeight:600,fontVariantNumeric:'tabular-nums',
                                  color:it.onBreak?'warning.onSoft':'text.secondary'}}>
                                  {it.breakMinutes+it.activeBreak}m
                                </Typography>}
                          </TableCell>

                          {/* Check-out */}
                          <TableCell align="right" sx={{py:1.25,display:{xs:'none',sm:'table-cell'}}}>
                            <Typography sx={{fontSize:'0.875rem',fontWeight:600,color:it.p.checkOut&&!onLeave?'text.primary':'text.disabled',fontVariantNumeric:'tabular-nums'}}>
                              {onLeave?'—':fmtTime(it.p.checkOut)||'—'}
                            </Typography>
                          </TableCell>

                          {/* Work hours + progress */}
                          <TableCell align="right" sx={{py:1.25}}>
                            {!onLeave&&(it.p.checkIn||it.workM>0)?(
                              <Box sx={{display:'inline-block',minWidth:88,textAlign:'right'}}>
                                <Typography sx={{fontSize:'0.875rem',fontWeight:700,color:'success.onSoft',
                                  fontVariantNumeric:'tabular-nums',lineHeight:1.2}}>
                                  {formatHHMMSS(it.p.checkOut?it.workM*60:it.workS)}
                                </Typography>
                                <LinearProgress variant="determinate" value={workPct} color="success"
                                  sx={{mt:0.5,height:4,borderRadius:2,bgcolor:'grey.100'}}/>
                              </Box>
                            ):<Typography sx={{color:'text.disabled'}}>—</Typography>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {derived.length===0&&(
                      <TableRow>
                        <TableCell colSpan={8} sx={{py:6,textAlign:'center',borderBottom:0}}>
                          <Box sx={{width:48,height:48,borderRadius:2,bgcolor:'grey.100',display:'inline-flex',alignItems:'center',justifyContent:'center',mb:1.5}}>
                            <PersonOffIcon sx={{fontSize:22,color:'text.disabled'}}/>
                          </Box>
                          <Typography sx={{fontSize:'0.9375rem',fontWeight:600,color:'text.primary',mb:0.5}}>
                            Asnjë punëtor nuk u gjet
                          </Typography>
                          <Typography variant="body2" sx={{color:'text.secondary'}}>
                            Ndrysho filtrat ose periudhën
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                {loadingPresence&&<LinearProgress sx={{height:2}}/>}
              </Card>
            </Stack>
          </Box>
        </>)}
      </Stack>
    </Box>

    {/* Worker Detail Dialog */}
    {selectedWorker&&(()=>{
      const it=selectedWorker;
      const p=presence[it.id]||it.p||{};
      const curStatus=statusFromPresence(p);
      const cfg=STATUS_CFG[curStatus]||STATUS_CFG.absent;
      const name=`${it.w?.FirstName||''} ${it.w?.LastName||''}`.trim();
      const init=name.split(' ').filter(Boolean).map(x=>x[0]).join('').slice(0,2).toUpperCase();
      const ob=(p.breaks||[]).some(b=>b.start&&!b.end);
      const canIn=!p.checkIn, canOut=!!p.checkIn&&!p.checkOut;
      const ws=workSeconds(p);
      const brkMin=calcBreakMinutes(p.breaks||[])+activeBreakMinutes(p);
      const onLeave=curStatus==='leave';
      // Toni i një butoni vjen nga statusi që prodhon veprimi, jo nga një hex i
      // zgjedhur për rastin: «Pauzë» duket si statusi «Në pauzë». «Përfundo»
      // rri neutral — mbarimi i ditës nuk është gabim, dhe e kuqja i mbetet
      // vetëm mungesës.
      const abtn=(label,onClick,icon,tone)=>(
        <Button size="small" onClick={()=>{onClick();setTimeout(()=>setSelectedWorker(prev=>prev?{...prev,p:presence[prev.id]||prev.p}:null),1500);}} startIcon={icon}
          sx={{textTransform:'none',fontSize:'0.74rem',fontWeight:700,px:1.5,py:0.6,
            borderRadius:2,color:tone.color,bgcolor:tone.bg,border:`1px solid ${tone.border}`,
            '&:hover':{bgcolor:tone.bg,borderColor:tone.color}}}>
          {label}
        </Button>
      );
      return(
        <Dialog open onClose={()=>setSelectedWorker(null)} maxWidth="sm" fullWidth
          PaperProps={{sx:{borderRadius:3,overflow:'hidden'}}}>
          {/* Koka e dialogut ishte e vetmja sipërfaqe e errët e mbetur brenda
              aplikacionit — një gradient `#0F172A→#1E293B` që e bënte dialogun
              të dukej si dritare e një produkti tjetër. Tani është e bardhë si
              faqja; ngjyrën e mban avatari, në tintin e statusit. */}
          <Box sx={{px:3,py:2,borderBottom:'1px solid',borderColor:'divider'}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{minWidth:0}}>
                <Avatar sx={{width:48,height:48,bgcolor:cfg.bg,color:cfg.color,fontSize:'1rem',fontWeight:800,
                  border:'1px solid',borderColor:cfg.border}}>{init}</Avatar>
                <Box sx={{minWidth:0}}>
                  <Typography sx={{fontSize:'1.0625rem',fontWeight:800,color:'text.primary',lineHeight:1.25}} noWrap>{name||'—'}</Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{mt:0.25}}>
                    {it.w?.Department&&(()=>{
                      const rawD=it.w.Department;
                      const dObj=departments.find(d=>String(d.Id||d.id)===String(rawD));
                      const dName=dObj?.Name||dObj?.name||(rawD&&!/^\d+$/.test(String(rawD).trim())?rawD:'');
                      return dName?<Typography sx={{fontSize:'0.75rem',fontWeight:500,color:'text.secondary'}}>{dName}</Typography>:null;
                    })()}
                    {it.w?.Position&&<>
                      <Box sx={{width:3,height:3,borderRadius:'50%',bgcolor:'grey.400'}}/>
                      <Typography sx={{fontSize:'0.75rem',color:'text.secondary'}}>{it.w.Position}</Typography>
                    </>}
                  </Stack>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{flexShrink:0}}>
                <Chip size="small" label={cfg.label}
                  icon={React.cloneElement(cfg.icon,{sx:{fontSize:'12px !important',color:`${cfg.color} !important`}})}
                  sx={{bgcolor:cfg.bg,color:cfg.color,fontWeight:600,fontSize:'0.6875rem',border:0,height:22,
                    '& .MuiChip-icon':{color:`${cfg.color} !important`,ml:0.625},
                    '& .MuiChip-label':{px:0.875}}}/>
                <IconButton onClick={()=>setSelectedWorker(null)} size="small" sx={{color:'text.secondary'}}><CloseIcon sx={{fontSize:18}}/></IconButton>
              </Stack>
            </Stack>
          </Box>
          <DialogContent sx={{p:0}}>
            {/* Emoji-t (✉️ 📞 📄) dolën: vizatohen ndryshe në çdo sistem dhe
                sjellin ngjyra që nuk janë tonat. */}
            {(it.w?.Email||it.w?.Phone||it.w?.ContractType)&&(
              <Box sx={{px:3,py:1.25,bgcolor:'grey.50',borderBottom:'1px solid',borderColor:'divider'}}>
                <Stack direction="row" spacing={2.5} flexWrap="wrap" rowGap={0.5}>
                  {[{Icon:MailOutlineIcon,value:it.w?.Email},
                    {Icon:LocalPhoneIcon, value:it.w?.Phone},
                    {Icon:DescriptionIcon,value:it.w?.ContractType},
                  ].filter(c=>c.value).map(({Icon,value})=>(
                    <Stack key={value} direction="row" spacing={0.625} alignItems="center">
                      <Icon sx={{fontSize:14,color:'text.disabled'}}/>
                      <Typography sx={{fontSize:'0.75rem',fontWeight:500,color:'text.secondary'}}>{value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
            <Box sx={{px:3,py:2.5}}>
              {/* «Dalja» kishte një rreth të kuq: dalja në fund të ditës nuk është
                  gabim. Tani ikona është ajo e statusit «Ka dalë», neutrale. */}
              <Stack direction={{xs:'column',sm:'row'}} spacing={1.25} sx={{mb:2.5}}>
                {[{label:'Hyrja',    value:p.checkIn?fmtTime(p.checkIn):'—',   Icon:LoginIcon,          tone:STATUS_CFG.present},
                  {label:'Dalja',    value:p.checkOut?fmtTime(p.checkOut):'—', Icon:LogoutIcon,         tone:STATUS_CFG.left},
                  {label:'Pauzë',    value:brkMin>0?`${brkMin}min`:'—',        Icon:FreeBreakfastIcon,  tone:STATUS_CFG.break},
                  {label:'Orë pune', value:p.checkIn?formatHHMMSS(ws):'—',     Icon:TimerIcon,          tone:STATUS_CFG.present},
                ].map(({label,value,Icon,tone})=>(
                  <Box key={label} sx={{flex:1,minWidth:0,p:1.25,borderRadius:2,bgcolor:'grey.50',border:'1px solid',borderColor:'divider'}}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{width:28,height:28,borderRadius:1.5,flexShrink:0,display:'grid',placeItems:'center',
                        bgcolor:tone.bg,color:tone.color}}>
                        <Icon sx={{fontSize:15}}/>
                      </Box>
                      <Box sx={{minWidth:0}}>
                        <Typography sx={{fontSize:'0.625rem',fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'text.secondary',lineHeight:1.4}}>
                          {label}
                        </Typography>
                        <Typography sx={{fontSize:'0.875rem',fontWeight:700,lineHeight:1.3,fontVariantNumeric:'tabular-nums',
                          color:value==='—'?'text.disabled':'text.primary'}} noWrap>
                          {value}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
              {(p.breaks||[]).length>0&&(
                <Box sx={{mb:2.5}}>
                  <Typography variant="caption" fontWeight={700} sx={{color:'text.secondary',textTransform:'uppercase',letterSpacing:0.7,mb:1,display:'block'}}>Pauza &amp; Takime sot</Typography>
                  <Stack spacing={0.6}>
                    {/* Lloji i pauzës ËSHTË çelës statusi te STATUS_CFG (`break`,
                        `lunch`, `short`, `meeting`), ndaj etiketa, ikona dhe toni
                        vijnë prej andej — jo nga dymbëdhjetë hex-e të shkruara
                        me dorë që rrëshqisnin nga pilulat e tabelës. */}
                    {(p.breaks||[]).map((b,i)=>{
                      const dur=b.start&&b.end?Math.round((new Date(b.end)-new Date(b.start))/60000):null;
                      const active=b.start&&!b.end;
                      const bcfg=STATUS_CFG[b.type]||STATUS_CFG.break;
                      return(
                        <Stack key={i} direction="row" spacing={1} alignItems="center"
                          sx={{p:'6px 10px',borderRadius:1.5,
                            bgcolor:active?bcfg.bg:'grey.50',
                            border:'1px solid',borderColor:active?bcfg.border:'divider'}}>
                          {React.cloneElement(bcfg.icon,{sx:{fontSize:13,color:bcfg.color}})}
                          <Typography variant="caption" fontWeight={700} sx={{color:bcfg.color,minWidth:112}}>
                            {bcfg.label}
                          </Typography>
                          <Typography variant="caption" sx={{color:'text.secondary',fontVariantNumeric:'tabular-nums'}}>
                            {fmtTime(b.start)} → {b.end?fmtTime(b.end):<Box component="span" sx={{color:'warning.onSoft',fontWeight:700}}>aktive</Box>}
                          </Typography>
                          {dur!==null&&<Typography variant="caption" sx={{ml:'auto',color:'text.secondary',fontWeight:600,fontVariantNumeric:'tabular-nums'}}>{dur}min</Typography>}
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              )}
              {!onLeave&&(
                <Box>
                  <Typography variant="caption" fontWeight={700} sx={{color:'text.secondary',textTransform:'uppercase',letterSpacing:0.7,mb:1,display:'block'}}>
                    Veprime{!canEdit&&<Box component="span" sx={{ml:1,color:'warning.onSoft',fontSize:'0.65rem',textTransform:'none',fontWeight:400}}>— vetëm WFM mund të editojë</Box>}
                  </Typography>
                  {canEdit&&(<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {canIn && abtn('Hyrja', ()=>onCheckIn(it.id), <LoginIcon sx={{fontSize:14}}/>, STATUS_CFG.present)}
                    {!canIn&&!ob&&canOut && abtn('Pauzë', ()=>onStartBreak(it.id), <PauseIcon sx={{fontSize:14}}/>, STATUS_CFG.break)}
                    {!canIn&&!ob&&canOut && abtn('Takim/Trajnim', ()=>onStartMeeting(it.id), <GroupsIcon sx={{fontSize:14}}/>, STATUS_CFG.meeting)}
                    {!canIn&&!ob&&canOut && abtn('Pauzë dreke', ()=>onStartLunch(it.id), <FreeBreakfastIcon sx={{fontSize:14}}/>, STATUS_CFG.lunch)}
                    {!canIn&&!ob&&canOut && abtn('Pushim shkurtër', ()=>onStartShortBreak(it.id), <TimerIcon sx={{fontSize:14}}/>, STATUS_CFG.short)}
                    {ob && abtn('Vazhdo', ()=>onEndBreak(it.id), <PlayArrowIcon sx={{fontSize:14}}/>, STATUS_CFG.present)}
                    {canOut && abtn('Përfundo', ()=>onCheckOut(it.id), <LogoutIcon sx={{fontSize:14}}/>, STATUS_CFG.left)}
                    {/* Dalja e regjistruar gabimisht kthehej vetëm me «Rivendos»,
                        që fshin gjithë ditën. Ngjarja `undo_checkout` ekzistonte
                        në backend por butoni jetonte te RowActions — kod i vdekur. */}
                    {p.checkOut&&!ob && abtn('Anulo dalje', ()=>onUndoCheckout(it.id), <LoginIcon sx={{fontSize:14}}/>, STATUS_CFG.present)}
                    <Button size="small" onClick={()=>setQrWorker({id:it.id,name})} startIcon={<QrCode2Icon sx={{fontSize:14}}/>}
                      sx={{textTransform:'none',fontSize:'0.74rem',fontWeight:700,px:1.5,py:0.6,
                        borderRadius:2,color:'primary.onSoft',bgcolor:'primary.soft',border:'1px solid',borderColor:'primary.main'}}>
                      QR
                    </Button>
                    <Button size="small" onClick={()=>{onReset(it.id);setSelectedWorker(null);}} startIcon={<RestartAltIcon sx={{fontSize:14}}/>}
                      sx={{textTransform:'none',fontSize:'0.74rem',fontWeight:700,px:1.5,py:0.6,
                        borderRadius:2,color:'text.secondary',bgcolor:'grey.50',border:'1px solid',borderColor:'divider'}}>
                      Rivendos
                    </Button>
                  </Stack>)}
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      );
    })()}

    {/* QR Dialog */}
    <Dialog open={!!qrWorker} onClose={()=>setQrWorker(null)} maxWidth="xs" fullWidth PaperProps={{sx:{borderRadius:3}}}>
      <DialogTitle sx={{display:'flex',alignItems:'center',justifyContent:'space-between',pb:0}}>
        <Stack direction="row" spacing={1} alignItems="center">
          <QrCode2Icon sx={{color:'primary.main'}}/>
          <Typography fontWeight={700}>QR Check-in — {qrWorker?.name}</Typography>
        </Stack>
        <IconButton size="small" onClick={()=>setQrWorker(null)}><CloseIcon fontSize="small"/></IconButton>
      </DialogTitle>
      <DialogContent sx={{p:0}}>
        {qrWorker&&<MyQRScreen workerId={parseInt(qrWorker.id,10)} workerName={qrWorker.name}/>}
      </DialogContent>
    </Dialog>

    {/* Toast */}
    <Snackbar open={toast.open} autoHideDuration={3000} onClose={()=>setToast(p=>({...p,open:false}))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert onClose={()=>setToast(p=>({...p,open:false}))} severity={toast.severity} variant="filled" sx={{width:'100%',borderRadius:2}}>
        {toast.message}
      </Alert>
    </Snackbar>
    </>
  );
}
